/** @author Rajkumar Rathod */
import {
  DEFAULT_API_BASE,
  DEFAULT_FLOW_PATHS,
  DEFAULT_HOST,
  DEFAULT_IFRAME_ALLOW,
  DEFAULT_READY_TIMEOUT_MS,
  DEFAULT_SANDBOX,
  DEFAULT_TRIGGERS,
  Z_INDEX,
} from './constants';
import { asArray, toOrigin } from './utils';
import type { ModalOptions, WidgetConfig } from './types';

export interface ResolvedModal extends Required<Omit<ModalOptions, 'width' | 'maxHeight'>> {
  width: string;
  maxHeight: string;
}

export interface ResolvedConfig {
  clientId: string;
  /** Falls back to `clientId` when the host does not set one. */
  tenantId: string;
  /** Clinic name shown in the embed. Empty when the tag omits it. */
  tenantName: string;
  origin: string;
  /** Full host string as given, so a base path like `https://ehr.io/app` survives. */
  host: string;
  /** REST API base the embed calls. Trailing slash stripped. */
  apiBaseUrl: string;
  flows: Record<string, string>;
  triggers: Record<string, string[]>;
  autoBind: boolean;
  theme: NonNullable<WidgetConfig['theme']>;
  locale: string;
  prefill: NonNullable<WidgetConfig['prefill']>;
  /**
   * The host's modal options as given, *not* resolved against the defaults.
   * Resolution happens per open, so `FLOW_MODAL_DEFAULTS` can sit underneath
   * them; resolving here would fill in every key and leave nothing for a flow
   * default to supply.
   */
  modal: ModalOptions;
  sandbox: string | false;
  allow: string | false;
  readyTimeoutMs: number;
  getAuthToken: WidgetConfig['getAuthToken'];
  debug: boolean;
}

const DEFAULT_MODAL: ResolvedModal = {
  width: 'min(600px, 100vw - 24px)',
  /*
   * No fixed cap. It used to be `min(760px, 100vh - 48px)`, and the 760px was
   * the binding half on every screen taller than 808px — so a form that grew
   * past it scrolled inside the panel on a display with room to spare
   * (ECRH-5992). The viewport is the only limit that means anything here; the
   * 24px is the gap `.wrap` leaves around the panel.
   */
  maxHeight: 'calc(100vh - 24px)',
  zIndex: Z_INDEX,
  closeOnBackdrop: true,
  closeOnEsc: true,
  showCloseButton: true,
  lockScroll: true,
  autoHeight: true,
  fullScreen: false,
};

/**
 * Per-flow modal defaults, applied beneath `config.modal` and `open({ modal })`
 * so a host can still override them.
 *
 * `find-doctor` is a browse grid — the design lays out four provider cards
 * across, which a 600px panel cannot show. Registration stays a centred card.
 */
export const FLOW_MODAL_DEFAULTS: Record<string, ModalOptions> = {
  'find-doctor': { fullScreen: true },
};

export function resolveModal(...layers: (ModalOptions | undefined)[]): ResolvedModal {
  return Object.assign({}, DEFAULT_MODAL, ...layers.filter(Boolean)) as ResolvedModal;
}

export function resolveConfig(input: WidgetConfig): ResolvedConfig {
  if (!input || typeof input !== 'object') {
    throw new Error('init() requires a config object');
  }
  const clientId = String(input.clientId || '').trim();
  if (!clientId) {
    throw new Error('init() requires a `clientId`. Get one from the EHR admin console.');
  }

  const host = (input.host || DEFAULT_HOST).replace(/\/+$/, '');
  const origin = toOrigin(host);
  // Falls back to the build-time API base, which itself falls back to the host — so a
  // deployment that serves the embed and the API together needs no configuration.
  const apiBaseUrl = (input.apiBaseUrl || DEFAULT_API_BASE || host).replace(/\/+$/, '');

  if (origin === window.location.origin) {
    // Same-origin defeats the isolation the iframe is there to provide, and the
    // handshake's source check would still pass — worth a loud warning.
    console.warn(
      '[ehr-widget] host origin equals the page origin; the embed will not be isolated from this page.'
    );
  }

  // Merge trigger selectors rather than replace, so passing one custom selector does
  // not silently unbind the documented default ids.
  const triggers: Record<string, string[]> = {};
  for (const [flow, selectors] of Object.entries(DEFAULT_TRIGGERS)) {
    triggers[flow] = selectors.slice();
  }
  for (const [flow, selectors] of Object.entries(input.triggers ?? {})) {
    const extra = asArray(selectors).filter((s) => typeof s === 'string' && s.trim());
    triggers[flow] = [...(triggers[flow] ?? []), ...extra];
  }

  return {
    clientId,
    tenantId: String(input.tenantId || '').trim() || clientId,
    tenantName: String(input.tenantName || '').trim(),
    host,
    origin,
    apiBaseUrl,
    flows: { ...DEFAULT_FLOW_PATHS, ...(input.flows ?? {}) },
    triggers,
    autoBind: input.autoBind !== false,
    theme: input.theme ?? 'auto',
    locale: input.locale || document.documentElement.lang || 'en',
    prefill: input.prefill ?? {},
    modal: input.modal ?? {},
    sandbox: input.sandbox === false ? false : input.sandbox || DEFAULT_SANDBOX,
    allow: input.allow === false ? false : input.allow || DEFAULT_IFRAME_ALLOW,
    readyTimeoutMs: Number(input.readyTimeoutMs) > 0 ? Number(input.readyTimeoutMs) : DEFAULT_READY_TIMEOUT_MS,
    getAuthToken: input.getAuthToken,
    debug: Boolean(input.debug),
  };
}

/**
 * Zero-JS integration path: read config off the <script> tag's data-* attributes.
 *
 *   <script src="…/widget.js"
 *           data-client-id="pk_live_x"
 *           data-tenant-id="northside"
 *           data-api-base="https://api.medarch.com/ehr/v1"
 *           data-theme="light"
 *           data-locale="en-IN"></script>
 *
 * `document.currentScript` is correct during synchronous execution; the querySelector
 * fallback covers async/defer, where currentScript is null.
 */
export function readScriptConfig(): Partial<WidgetConfig> & { autoInit: boolean } {
  const el =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-client-id][src*="widget"]');

  if (!el) return { autoInit: false };

  const d = el.dataset;
  const out: Partial<WidgetConfig> & { autoInit: boolean } = {
    // Opt out with data-auto-init="false" when you want to call init() yourself.
    autoInit: Boolean(d['clientId']) && d['autoInit'] !== 'false',
  };

  if (d['clientId']) out.clientId = d['clientId'];
  if (d['tenantId']) out.tenantId = d['tenantId'];
  if (d['tenantName']) out.tenantName = d['tenantName'];
  if (d['host']) out.host = d['host'];
  if (d['apiBase']) out.apiBaseUrl = d['apiBase'];
  if (d['theme']) out.theme = d['theme'] as WidgetConfig['theme'];
  if (d['locale']) out.locale = d['locale'];
  if (d['debug'] === 'true') out.debug = true;
  if (d['autoBind'] === 'false') out.autoBind = false;

  return out;
}
